/**
 * Auth helpers wrapping the session/queries layer.
 * Returns the `account` + `isAuthenticated` flags components consume.
 */
import type { HouseholdId } from '@shared/types';
import { ApiError } from './api';
import { useSession } from './queries';

export interface AuthState {
  account: {
    id: string;
    email: string;
    display_name: string | null;
  } | null;
  active_household_id: HouseholdId | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * The session query may resolve as 401 — we surface it cleanly so route
   * guards can redirect without re-throwing.
   */
  error: ApiError | null;
}

export function useAuth(): AuthState {
  const { data, isPending, error } = useSession();

  const apiError =
    error instanceof ApiError
      ? error
      : error
        ? new ApiError({ code: 'INTERNAL', message: error.message, status: 0 })
        : null;

  if (data) {
    return {
      account: data.account,
      active_household_id: data.active_household_id,
      isLoading: false,
      isAuthenticated: true,
      error: null,
    };
  }

  return {
    account: null,
    active_household_id: null,
    isLoading: isPending,
    isAuthenticated: false,
    error: apiError,
  };
}
